import { AssetFile, AssetImage, EnumDKeyAssetType } from "gql/generated";
import JSZip from "jszip";
import { getCloudinary } from "shared/cloudinary";
import { format } from "@cloudinary/transformation-builder-sdk/actions/delivery";
import { getFileExtensionFromMimeType } from "./getFileExtensionFromMimeType";
import { PostHogExceptionDataProps } from "shared/hooks";

const isMuxVideo = (asset: Partial<AssetImage | AssetFile>) => {
  if (asset.type !== EnumDKeyAssetType.AssetImage) return false;
  const mux = (asset as AssetImage).mux;
  return Boolean(mux?.playbackId || mux?.assetId);
};

/**
 * True when every selected asset is a Mux video (nothing downloadable as raw bytes).
 */
export const isZipDownloadDisabledForAssets = (
  assets: Partial<AssetImage | AssetFile>[],
): boolean =>
  assets.length > 0 && assets.every(asset => isMuxVideo(asset));

/**
 * Downloads multiple assets as a zip file.
 * Mux-hosted videos are skipped (no raw MP4 renditions).
 * @param assets - Array of assets to download
 * @param folderName - Name of the folder inside the zip (default: "assets")
 * @param filename - Name of the zip file (default: timestamp-based)
 */
export const downloadAssetsAsZip = async (
  assets: Partial<AssetImage | AssetFile>[],
  folderName: string = "assets",
  filename?: string,
  trackPostHogException?: (
    error: unknown,
    data: PostHogExceptionDataProps,
  ) => void,
): Promise<void> => {
  if (!assets || assets.length === 0) return;

  const downloadable = assets.filter(asset => !isMuxVideo(asset));
  if (downloadable.length === 0) return;

  try {
    const zip = new JSZip();
    const assetFolder = zip.folder(folderName);

    const urlGenerator = (asset: Partial<AssetImage | AssetFile>) =>
      getCloudinary(asset._id).image(asset._id).delivery(format("auto")).toURL();

    const fileNameGenerator = (
      asset: Partial<AssetImage | AssetFile>,
      index: number,
      blobType: string,
    ) => {
      const isImage = asset.type === EnumDKeyAssetType.AssetImage;
      const isVideo =
        asset.type === EnumDKeyAssetType.AssetImage &&
        (asset as AssetImage).isVideo;
      const fileType = isImage ? (isVideo ? "video" : "photo") : "file";
      const extension = getFileExtensionFromMimeType(blobType);
      return `${fileType}_${index + 1}.${extension}`;
    };

    // Download all assets and add them to the zip
    const downloadPromises = downloadable.map(async (asset, index) => {
      const assetUrl = urlGenerator(asset);
      const response = await fetch(assetUrl);
      const blob = await response.blob();

      const fileName = fileNameGenerator(asset, index, blob.type);
      assetFolder?.file(fileName, blob);
    });

    await Promise.all(downloadPromises);

    // Generate the zip file
    const zipBlob = await zip.generateAsync({ type: "blob" });

    // Create download link
    const url = window.URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `assets_${Date.now()}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    trackPostHogException?.(error, {
      fileName: "downloadAssetsAsZip.ts",
      method: "downloadAssetsAsZip",
      message: "Error downloading assets as zip",
    });
    console.error("Error downloading assets as zip:", error);
    throw error;
  }
};
